export type NavIcon =
  | 'dashboard'
  | 'portfolio'
  | 'ai-optimizer'
  | 'harvestable-losses'
  | 'transactions'
  | 'spam-shield'
  | 'tax-center'
  | 'tax-accountant'
  | 'zk-proofs'
  | 'rwa-assets'
  | 'integrations'
  | 'residency'
  | 'referral'
  | 'settings'
  | 'subscription'
  | 'support';

export interface NavItem {
  label: string;
  icon: NavIcon;
  route: string;
  comingSoon?: boolean;
  // When set, clicking the item opens a modal instead of navigating (route is ignored).
  modal?: 'referral';
  // Show this item only when subscription/payment mode is on. Referral rewards depend on paid
  // subscriptions, so the entry point is hidden while billing is disabled.
  requiresSubscriptionMode?: boolean;
  // Restrict this item to specific tax countries (uppercase codes). Omit → shown for
  // everyone. Harvestable Losses lists the jurisdictions that have a defined state:
  // US/DE/AU (full flow) plus IN/SG (visible with a "why it doesn't apply" explainer). Other
  // countries have no explainer yet, so the item stays hidden for them. Add a country
  // here when its harvest rule ships.
  countries?: string[];
  // Highlight only on this exact route — for an item whose route prefixes another item's.
  exact?: boolean;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
  // When true, the group renders as a collapsed "More ▾" toggle (items hidden until expanded).
  // Used to tuck away not-yet-shipped features so the primary nav fits without scrolling.
  collapsible?: boolean;
}

// Consolidated to 4 primary groups + a collapsible "More" for coming-soon items, so the whole
// sidebar fits a laptop viewport without scrolling.
export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'OVERVIEW',
    items: [
      { label: 'Dashboard', icon: 'dashboard', route: '/app/dashboard' },
      { label: 'Portfolio', icon: 'portfolio', route: '/app/portfolio' }
    ]
  },
  {
    label: 'TAX & COMPLIANCE',
    items: [
      { label: 'Transactions', icon: 'transactions', route: '/app/transactions', exact: true },
      { label: 'Spam Shield', icon: 'spam-shield', route: '/app/transactions/spam' },
      { label: 'Tax Center', icon: 'tax-center', route: '/app/tax-center' },
      // Residency decides which country's rules, tax year and currency each period uses, so it
      // sits with the tax pages rather than under "More".
      { label: 'Tax Residency', icon: 'residency', route: '/app/residency' },
      { label: 'Harvestable Losses', icon: 'harvestable-losses', route: '/app/harvestable-losses', countries: ['US', 'DE', 'AU', 'UK', 'CA', 'IE', 'IN', 'SG'] },
      // Was buried in the profile flyout, where nothing about "Tax Accountant" suggested it lived
      // behind an avatar. It belongs beside the other tax destinations.
      { label: 'Tax Accountant', icon: 'tax-accountant', route: '/app/accountant-access' }
    ]
  },
  {
    label: 'GROW',
    items: [
      { label: 'Integrations', icon: 'integrations', route: '/app/integrations' },
      { label: 'RWA Assets', icon: 'rwa-assets', route: '/app/rwa-assets' },
      { label: 'Refer & Earn', icon: 'referral', route: '/app/referral', modal: 'referral', requiresSubscriptionMode: true }
    ]
  },
  {
    label: 'SETTINGS',
    items: [
      { label: 'Settings', icon: 'settings', route: '/app/settings' },
      // Also lifted out of the profile flyout. Same gate as the referral entry — there is nothing
      // to subscribe to while billing is off.
      { label: 'Pricing & Plans', icon: 'subscription', route: '/app/pricing' },
      // Named for where it goes, not for what the floating button does. This route is the review
      // screen listing issues already raised; reporting a new one is the FAB in ShellLayout.
      { label: 'My Issues', icon: 'support', route: '/app/support/issues' }
    ]
  },
  {
    label: 'More',
    collapsible: true,
    items: [
      { label: 'AI Optimizer', icon: 'ai-optimizer', route: '/app/ai-optimizer', comingSoon: true },
      { label: 'ZK Proofs', icon: 'zk-proofs', route: '/app/zk-proofs', comingSoon: true }
    ]
  }
];

/** Flat list — kept for backwards-compat with code that still expects NAV_ITEMS. */
export const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((g) => g.items);
